import Quill from 'quill/core';
import React, { forwardRef, useContext, useState } from 'react';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import useAppSelector, {
  useAppDispatch,
} from '../../../app/redux/useAppSelector';
import Menu from '../../../components/Menu';
import SVGIcon from '../../../components/SVGIcon';
import overflowMenuIcon from '../../../icons/non-prefilled/16x16/overflowMenu.svg';
import useIsPublicView from '../../../utils/useIsPublicView';
import { EditorActions } from '../../editorSlice';
import { useQuill } from '../../QuillContext';
import type { Blot } from '../../types';
import { editIcon } from '../editorIcons';

interface ToolbarEditButtonProps {
  targetBlot?: Blot;
  onClick?: () => void;
}

const ToolbarEditButton = ({ targetBlot, onClick }: ToolbarEditButtonProps) => {
  const { quill } = useQuill();
  const dispatch = useAppDispatch();
  const isEditable = useAppSelector(
    (state) => !state.session.organization.isLocked && state.editor.editable,
  );
  const isPublicView = useIsPublicView();
  return (
    <ToolbarButton
      icon={editIcon}
      onClick={() => {
        if (quill && targetBlot) {
          const index = targetBlot.offset(quill.scroll);
          if (index >= 0) {
            quill.setSelection(index, targetBlot.length(), Quill.sources.USER);
          }
        }
        dispatch(EditorActions.readOnly(false));
        onClick?.();
      }}
      shortcut={isPublicView || !isEditable ? undefined : 'e or i'}
      tooltip={
        isEditable
          ? 'Enter edit mode'
          : 'This post is read-only\nand cannot be edited'
      }
      disabled={!isEditable}
      testId="toolbarEditButton"
    >
      Edit
    </ToolbarButton>
  );
};

interface ToolbarOverflowMenuProps {
  children: React.ReactNode;
  rightAlignOverflowMenu?: boolean;
  onMenuOpenChange?: (isOpen: boolean) => void;
}

export const ToolbarOverflowMenu = forwardRef<
  HTMLDivElement,
  ToolbarOverflowMenuProps
>(({ children, rightAlignOverflowMenu, onMenuOpenChange }, ref) => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  return (
    <ToolbarButtonGroup ref={ref}>
      <ToolbarButton
        popOverHideCaret
        rightAlignPopOver={rightAlignOverflowMenu}
        icon={<SVGIcon>{overflowMenuIcon}</SVGIcon>}
        renderPopOver={(onClose) => (
          <Menu>
            <ToolbarOverflowMenuCloseContext.Provider value={onClose}>
              {children}
            </ToolbarOverflowMenuCloseContext.Provider>
          </Menu>
        )}
        popOverOpen={isOverflowMenuOpen}
        onPopOverOpenChange={(open) => {
          setIsOverflowMenuOpen(open);
          onMenuOpenChange?.(open);
        }}
        testId="toolbarOverflowMenuButton"
        aria-label="More options"
      />
    </ToolbarButtonGroup>
  );
});

type ToolbarProps = {
  children?: React.ReactNode;
  hideEditButton?: boolean;
  onEditButtonClick?: () => void;
  targetBlot?: Blot;
  overflowMenu?: React.ReactNode;
};

const Toolbar = ({
  children,
  hideEditButton,
  onEditButtonClick,
  targetBlot,
  overflowMenu,
}: ToolbarProps) => {
  const isPublicView = useIsPublicView();
  const isEditable = useAppSelector((state) => state.editor.editable);
  const { readOnly } = useQuill();

  return (
    <>
      {(!isPublicView || isEditable) && readOnly && !hideEditButton && (
        <ToolbarButtonGroup>
          <ToolbarEditButton
            targetBlot={targetBlot}
            onClick={onEditButtonClick}
          />
        </ToolbarButtonGroup>
      )}
      {children}
      {overflowMenu}
    </>
  );
};

const ToolbarOverflowMenuCloseContext = React.createContext(() => {});
export const useToolbarOverflowMenuClose = () =>
  useContext(ToolbarOverflowMenuCloseContext);

export default Toolbar;
